// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyOrder, TyOrderItem, TyOrderStatus } from "@/types";
import OrderContacts from "@/UI/components/OrderContacts";
import OrderTimeline from "@/UI/components/OrderTimeline";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { formatOrderTime, pickLangValue, readLangs } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type OrderPreviewPopupDetails = {
    order: TyOrder;
    /** False for a client who may read orders but not act on them — the trail becomes a picture. */
    editable: boolean;
    onUpdated: (order: TyOrder) => void;
    onEdit: (order: TyOrder) => void;
};

type Props = {
    onClose: () => void;
    details?: OrderPreviewPopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One order, opened. The card in the list is a summary built to be scanned; this is where an order is
 * actually worked on, so it has the room the card deliberately does not.
 *
 * **Status lives here and nowhere else.** The trail is the control: tapping a step moves the order to
 * it, forwards or back, and it only ever offers the steps that order's own kind has. That is one
 * place to look and one place to press, instead of a button on the card whose label changed with
 * every status and made every card a different height.
 *
 * It keeps its own copy of the order so the screen behind it does not have to re-open it after every
 * change, and hands each new version back so the list stays in step.
 */
export default function OrderPreviewPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { addComp, removeComp } = usePopup();

    const [order, setOrder] = useState<TyOrder | null>(details?.order ?? null);
    const [showContacts, setShowContacts] = useState(false);

    const {
        data: statusData,
        loading: saving,
        error,
        invoke: save,
    } = useFetch<TyApiResponse<{ order: TyOrder }>>(`/orders/${order?._id ?? "x"}/status`, { method: "PUT" }, false);

    // `invoke` only starts the request — every form in the app reads the answer from an effect.
    useEffect(() => {
        const updated = statusData?.data?.order;
        if (!updated) return;
        setOrder((prev) => (prev ? { ...prev, ...updated } : updated));
        details?.onUpdated(updated);
    }, [statusData]);

    if (!order) return null;

    const isAborted = constants.orderAbortStatuses.includes(order.status as "rejected" | "cancelled");
    const isOver = isAborted || order.status === "completed";
    const isPending = order.status === "pending";

    const branchName = order.branch ? pickLangValue(readLangs(order.branch.name, ["ar", "en"]), lang) : t.orders.mainMenu;

    /* Each line is read in the language this dashboard is in, out of the snapshot the order kept. */
    const itemName = (item: TyOrderItem) => pickLangValue(readLangs(item.name, ["ar", "en"]), lang);
    const itemLabel = (item: TyOrderItem) => pickLangValue(readLangs(item.label, ["ar", "en"]), lang);
    const locale = lang === "ar" ? "ar-EG" : "en-GB";
    const placedAt = formatOrderTime(order.createdAt, lang);

    const customer = order.customer;
    const isReturning = (customer?.ordersCount ?? 0) > 1;
    const firstOrderAt = customer?.firstOrderAt
        ? new Date(customer.firstOrderAt).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" })
        : "—";

    // --- Handlers --------------------------------------------------------------------------------

    /** Every move is confirmed, forwards and back alike: what happens here is visible to the customer. */
    const prompt = (next: TyOrderStatus, message: string, danger: boolean, confirmLabel: string) => {
        removeComp("confirm-order-status");
        addComp({
            name: "confirm-order-status",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message,
                icon: danger ? "/icons/cancel-icon.svg" : "/icons/check-icon.svg",
                variant: danger ? "danger" : "primary",
                confirmLabel,
                cancelLabel: t.orders.editCancelLabel,
                onConfirm: () => save({ method: "PUT", data: { status: next } }),
            },
        });
    };

    const handlePick = (next: TyOrderStatus) => {
        const flow = constants.orderStatusFlow[order.type] ?? [];
        const back = flow.indexOf(next) < flow.indexOf(order.status);
        const copy = back ? t.orders.backPrompt : t.orders.movePrompt;
        prompt(next, copy.replace("{status}", t.orders.status[next]), false, back ? t.orders.backLabel : t.orders.moveLabel);
    };

    const handleAbort = () => {
        const next: TyOrderStatus = isPending ? "rejected" : "cancelled";
        prompt(next, isPending ? t.orders.rejectPrompt : t.orders.cancelPrompt, true, isPending ? t.orders.rejectLabel : t.orders.cancelLabel);
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="order-preview">
            <header className="order-preview-head">
                <div className="order-preview-head-text">
                    <div className="order-preview-title-row">
                        <p className="order-preview-number">#{order.orderNumber}</p>
                        <span className="order-preview-status" data-status={order.status}>
                            {t.orders.status[order.status]}
                        </span>
                    </div>
                    <p className="order-preview-sub">
                        {placedAt} · {t.orders.type[order.type] ?? order.type} · {branchName}
                    </p>
                </div>

                <button type="button" className="order-preview-close" onClick={onClose} aria-label={t.orders.editCancelLabel}>
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </header>

            <div className="order-preview-body">
                <section className="order-preview-panel">
                    <p className="order-preview-panel-title">{t.orders.progressTitle}</p>
                    <OrderTimeline
                        type={order.type}
                        status={order.status}
                        onPick={details?.editable && !isAborted ? handlePick : undefined}
                        disabled={saving}
                    />
                    {details?.editable && !isAborted && <p className="order-preview-hint">{t.orders.progressHint}</p>}
                </section>

                {(order.tableNumber || order.address) && (
                    <section className="order-preview-panel">
                        <p className="order-preview-panel-title">{order.type === "delivery" ? t.orders.addressLabel : t.orders.tableLabel}</p>
                        <p className="order-preview-where">{order.type === "delivery" ? order.address : order.tableNumber}</p>
                    </section>
                )}

                <section className="order-preview-panel">
                    <p className="order-preview-panel-title">{t.orders.itemsTitle}</p>
                    <ul className="order-preview-items">
                        {order.items.map((item, i) => (
                            <li key={`${item.product}-${item.priceId}-${i}`} className="order-preview-item">
                                <span className="order-preview-item-qty">×{item.quantity}</span>
                                <span className="order-preview-item-name">
                                    {itemName(item)}
                                    {itemLabel(item) && <span className="order-preview-item-label"> — {itemLabel(item)}</span>}
                                </span>
                                <span className="order-preview-item-price">{item.price * item.quantity}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="order-preview-total">
                        <span>{t.orders.totalLabel}</span>
                        <span className="order-preview-total-value">{order.totalPrice}</span>
                    </div>
                </section>

                {/* Last, under the order itself: who it is for matters once what they asked for has been read. */}
                <section className="order-preview-panel">
                    <div className="order-preview-panel-head">
                        <p className="order-preview-panel-title">{t.orders.customerTitle}</p>
                        {isReturning && <span className="order-preview-returning">{t.orders.returningBadge}</span>}
                    </div>

                    <OrderContacts phone={order.customerPhone} isOpen={showContacts} onToggle={() => setShowContacts((prev) => !prev)} size="lg" />

                    <div className="order-preview-facts">
                        <div className="order-preview-fact">
                            <span className="order-preview-fact-label">{t.orders.firstOrderLabel}</span>
                            <span className="order-preview-fact-value">{firstOrderAt}</span>
                        </div>
                        <div className="order-preview-fact">
                            <span className="order-preview-fact-label">{t.orders.spentLabel}</span>
                            <span className="order-preview-fact-value">
                                {customer?.spentTotal ?? 0}
                                <span className="order-preview-fact-note">
                                    {t.orders.acrossOrders.replace("{count}", String(customer?.ordersCount ?? 0))}
                                </span>
                            </span>
                        </div>
                    </div>
                </section>

                {order.edits && order.edits.length > 0 && (
                    <p className="order-preview-note">{t.orders.editedNote.replace("{count}", String(order.edits.length))}</p>
                )}

                {error && <p className="order-preview-error">{error}</p>}
            </div>

            {details?.editable && (
                <footer className="order-preview-foot">
                    {!isOver && (
                        <button type="button" className="order-preview-btn order-preview-btn--danger" onClick={handleAbort} disabled={saving}>
                            {isPending ? t.orders.rejectLabel : t.orders.cancelLabel}
                        </button>
                    )}
                    <button
                        type="button"
                        className="order-preview-btn order-preview-btn--primary"
                        onClick={() => details.onEdit(order)}
                        disabled={saving}
                    >
                        {t.orders.editLabel}
                    </button>
                </footer>
            )}
        </div>
    );
}
